import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CidadeService } from '../../../services/configuracoes/cidade.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { TipoPessoa } from '../../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../../enums/sexo.enum';
import { PessoaDTO, PessoaFormDTO } from '../../../models/clientes/pessoa.model';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import { PessoaRucResultado } from '../../../models/pessoas/pessoa-ruc-resultado.model';
import { PaisDDI } from '../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../shared/data/paises-ddi.data';
import { RucBuscaComponent } from '../../../shared/components/ruc-busca/ruc-busca.component';

@Component({
  selector: 'app-fornecedor',
  templateUrl: './fornecedor.component.html',
  styleUrls: ['./fornecedor.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class FornecedorComponent implements OnInit, OnDestroy {
  form: PessoaFormDTO = this.createEmptyForm();
  cidadeList: { label: string; value: string; estado: string }[] = [];
  estadoCidade!: string;

  tipoPessoaOptions: { value: TipoPessoa; label: string }[] = [];
  tipoNaturezaFiscalOptions: { value: TipoNaturezaFiscal; label: string }[] = [];
  sexoOptions: { value: Sexo; label: string }[] = [];

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  @Input() isDialog: boolean = false;

  @ViewChild('rucBuscaRef') rucBuscaRef?: RucBuscaComponent;

  // Busca automatica por CI
  private ciInput$ = new Subject<string>();
  private ciDestroy$ = new Subject<void>();

  // Dialog pessoa ja cadastrada
  pessoaJaCadastradaDialogVisible = false;
  pessoaJaCadastradaMensagem = '';
  pessoaJaCadastradaId: number | null = null;

  // Textos traduzidos
  texts = {
    titulo: 'Adicionar Novo Fornecedor',
    subtitulo: 'Preencha os dados do novo fornecedor',
    identificacao: 'Identificacao',
    contato: 'Contato',
    dadosPessoais: 'Dados Pessoais',
    endereco: 'Endereco'
  };

  labels = {
    ruc: 'RUC',
    ci: 'CI',
    nome: 'Nome',
    nomeFantasia: 'Nome Fantasia',
    tipoPessoa: 'Tipo Pessoa',
    tipoNaturezaFiscal: 'Tipo Natureza Fiscal',
    ddi: 'DDI',
    telefone: 'Telefone',
    email: 'E-mail',
    dataNascimento: 'Data de Nascimento',
    sexo: 'Sexo',
    cidade: 'Cidade',
    estado: 'Estado',
    endereco: 'Endereco',
    numero: 'Numero'
  };

  placeholders = {
    ruc: 'RUC do fornecedor',
    ci: 'CI',
    nome: 'Nome do fornecedor',
    nomeFantasia: 'Nome Fantasia',
    tipoPessoa: '-- Selecione o tipo de pessoa --',
    tipoNaturezaFiscal: '-- Selecione o tipo de natureza fiscal --',
    ddi: 'DDI',
    telefone: 'Numero de telefone',
    email: 'E-mail do fornecedor',
    sexo: '-- Selecione o sexo --',
    cidade: '-- Selecione a cidade --',
    endereco: 'Endereco do fornecedor',
    numero: 'Numero'
  };

  botoes = {
    voltar: 'Voltar',
    enviar: 'Enviar'
  };

  isFormSubmitted = false;
  submitting = false;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private cidadeService: CidadeService,
    private clienteService: PessoaService,
    private router: Router
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit() {
    this.findCidades();
    this.loadTranslations();
    this.buildTipoPessoaOptions();
    this.buildTipoNaturezaFiscalOptions();
    this.buildSexoOptions();
    this.inicializarDebounceCI();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
      this.buildTipoPessoaOptions();
      this.buildTipoNaturezaFiscalOptions();
      this.buildSexoOptions();
    });
  }

  private loadTranslations() {
    this.texts = {
      titulo: this.translate.instant('fornecedor-add.texts.cadastro_de_fornecedor'),
      subtitulo: this.translate.instant('fornecedor-add.texts.subtitulo'),
      identificacao: this.translate.instant('fornecedor-add.texts.identificacao'),
      contato: this.translate.instant('fornecedor-add.texts.contato'),
      dadosPessoais: this.translate.instant('fornecedor-add.texts.dados_pessoais'),
      endereco: this.translate.instant('fornecedor-add.texts.endereco')
    };

    this.labels = {
      ruc: this.translate.instant('fornecedor-add.labels.ruc'),
      ci: this.translate.instant('fornecedor-add.labels.ci'),
      nome: this.translate.instant('fornecedor-add.labels.nome'),
      nomeFantasia: this.translate.instant('fornecedor-add.labels.nome_fantasia'),
      tipoPessoa: this.translate.instant('fornecedor-add.labels.tipo_pessoa'),
      tipoNaturezaFiscal: this.translate.instant('fornecedor-add.labels.tipo_natureza_fiscal'),
      ddi: this.translate.instant('fornecedor-add.labels.ddi'),
      telefone: this.translate.instant('fornecedor-add.labels.telefone'),
      email: this.translate.instant('fornecedor-add.labels.e_mail'),
      dataNascimento: this.translate.instant('fornecedor-add.labels.data_de_nascimento'),
      sexo: this.translate.instant('fornecedor-add.labels.sexo'),
      cidade: this.translate.instant('fornecedor-add.labels.cidade'),
      estado: this.translate.instant('fornecedor-add.labels.estado'),
      endereco: this.translate.instant('fornecedor-add.labels.endereco'),
      numero: this.translate.instant('fornecedor-add.labels.numero')
    };

    this.placeholders = {
      ruc: this.translate.instant('fornecedor-add.placeholders.ruc_do_fornecedor'),
      ci: this.translate.instant('fornecedor-add.placeholders.ci'),
      nome: this.translate.instant('fornecedor-add.placeholders.nome_do_fornecedor'),
      nomeFantasia: this.translate.instant('fornecedor-add.placeholders.nome_fantasia'),
      tipoPessoa: this.translate.instant('fornecedor-add.placeholders.selecione_o_tipo_de_pessoa'),
      tipoNaturezaFiscal: this.translate.instant('fornecedor-add.placeholders.selecione_o_tipo_de_natureza_fiscal'),
      ddi: this.translate.instant('fornecedor-add.placeholders.ddi'),
      telefone: this.translate.instant('fornecedor-add.placeholders.telefone'),
      email: this.translate.instant('fornecedor-add.placeholders.e_mail_do_fornecedor'),
      sexo: this.translate.instant('fornecedor-add.placeholders.selecione_o_sexo'),
      cidade: this.translate.instant('fornecedor-add.placeholders.selecione_a_cidade'),
      endereco: this.translate.instant('fornecedor-add.placeholders.endereco_do_fornecedor'),
      numero: this.translate.instant('fornecedor-add.placeholders.numero_do_endereco')
    };

    this.botoes = {
      voltar: this.translate.instant('fornecedor-add.botoes.voltar'),
      enviar: this.translate.instant('fornecedor-add.botoes.enviar')
    };
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.ciDestroy$.next();
    this.ciDestroy$.complete();
  }

  private buildTipoPessoaOptions() {
    this.tipoPessoaOptions = Object.values(TipoPessoa).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`enums.tipo_pessoa.${tipo.toLowerCase()}`)
    }));
  }

  private buildTipoNaturezaFiscalOptions() {
    this.tipoNaturezaFiscalOptions = Object.values(TipoNaturezaFiscal).map(natureza => ({
      value: natureza,
      label: this.translate.instant(`enums.tipo_natureza_fiscal.${natureza.toLowerCase()}`)
    }));
  }

  private buildSexoOptions() {
    this.sexoOptions = Object.values(Sexo).map(sexo => ({
      value: sexo,
      label: this.translate.instant(`enums.sexo.${sexo.toLowerCase()}`)
    }));
  }

  findCidades(): Promise<void> {
    return new Promise((resolve) => {
      this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
        this.cidadeList = response.content.map((cidade: any) => ({
          label: cidade.nome,
          value: String(cidade.id),
          estado: cidade.nomeEstado
        }));
        resolve();
      });
    });
  }

  onSelecionarCidade(event: any) {
    // O showClear do p-dropdown emite onChange com value null: sem cidade, o estado tambem some.
    const valorCidade = event?.value?.value;
    if (valorCidade == null) {
      this.estadoCidade = '';
      return;
    }
    const cidadeSelecionada = this.cidadeList.find(cidade => cidade.value === valorCidade);
    if (cidadeSelecionada) {
      this.estadoCidade = cidadeSelecionada.estado;
    }
  }

  getDropItems<T extends object>(enumType: T): { label: string; value: T[keyof T] }[] {
    return Object.values(enumType).map((item) => ({
      label: String(item),
      value: item as T[keyof T],
    }));
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;
    if (valor) {
      const partes = valor.split('-');
      if (partes.length > 0 && partes[0].length > 4) {
        // Truncar o ano para 4 digitos
        partes[0] = partes[0].substring(0, 4);
        const novaData = partes.join('-');
        input.value = novaData;
        this.form.dataNascimento = novaData;
      }
    }
  }

  validarDataNascimento(): boolean {
    if (!this.form.dataNascimento) {
      return true; // Campo opcional, vazio e valido
    }
    const partes = this.form.dataNascimento.split('-');
    if (partes.length > 0 && partes[0].length > 4) {
      return false; // Ano com mais de 4 digitos
    }
    return true;
  }

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || !this.form.sexo || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('fornecedor-add.validacoes.campos_obrigatorios'),
      });
      return;
    }

    if (!this.validarDataNascimento()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('fornecedor-add.validacoes.data_nascimento_invalida'),
      });
      return;
    }

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email,
      ddi: this.paisSelecionado.ddi.replace('+', '') || '595',
      telefone: this.form.telefone?.replace(/\D/g, '') || undefined,
      endereco: this.form.endereco,
      numero: this.form.numero,
      cidade_id: Number(this.form.cidade?.value),
      categoriaCliente: false,
      categoriaFornecedor: true,
      categoriaVendedor: false,
      tipoPessoa: this.form.tipoPessoa.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal.value,
      dataNascimento: this.form.dataNascimento,
      sexo: this.form.sexo.value,
    };

    this.submitting = true;
    this.clienteService.post('/pessoas', body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('fornecedor-add.mensagens.cadastrado_com_sucesso'),
        });
        this.rucBuscaRef?.reset();
        this.form = this.createEmptyForm();
        this.paisSelecionado = PAISES_DDI[0];
        this.findCidades();
        this.router.navigate(['lista-de-fornecedores']);
      },
      error: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('fornecedor-add.mensagens.erro_ao_cadastrar'),
        });
      },
    });
    this.isFormSubmitted = false;
  }

  createEmptyForm(): PessoaFormDTO {
    const sexo = this.getDropItems(Sexo);
    const tipoPessoa = this.getDropItems(TipoPessoa);
    const tipoNaturezaFiscal = this.getDropItems(TipoNaturezaFiscal);

    return {
      nome: '',
      fantasia: '',
      ruc: '',
      ci: '',
      email: '',
      ddi: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: false,
      categoriaFornecedor: true,
      categoriaVendedor: false,
      tipoPessoa: tipoPessoa[0],
      tipoNaturezaFiscal: tipoNaturezaFiscal[0],
      dataNascimento: '',
      sexo: sexo[0],
    };
  }

  onRucPessoaConfirmada(resultado: PessoaRucResultado): void {
    this.form.ruc = resultado.ruc || '';
    if (resultado.nome) this.form.nome = resultado.nome;
    if (resultado.nome && !this.form.fantasia) this.form.fantasia = resultado.nome;
    if (resultado.ddi) {
      const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) this.paisSelecionado = paisEncontrado;
    }
    if (resultado.telefone) this.form.telefone = resultado.telefone;
    if (resultado.cidade?.id) {
      const cidadeEncontrada = this.cidadeList.find(c => c.value === String(resultado.cidade!.id));
      if (cidadeEncontrada) {
        this.form.cidade = { label: cidadeEncontrada.label, value: Number(cidadeEncontrada.value) };
        this.estadoCidade = cidadeEncontrada.estado;
      }
    }
  }

  onRucPessoaVinculada(pessoa: PessoaDTO): void {
    this.pessoaJaCadastradaId = pessoa.id ?? null;
    this.pessoaJaCadastradaMensagem = this.translate.instant('fornecedor-add.mensagens.pessoa_ja_cadastrada', { nome: pessoa.nome });
    this.pessoaJaCadastradaDialogVisible = true;
  }

  onRucLimpo(): void {
    this.form.nome = '';
    this.form.fantasia = '';
    this.form.telefone = '';
    this.form.cidade = undefined;
    this.estadoCidade = '';
    this.paisSelecionado = PAISES_DDI[0];
  }

  onCiInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = (input.value || '').trim();
    if (!valor || valor.length < 3) return;
    this.ciInput$.next(valor);
  }

  private inicializarDebounceCI(): void {
    this.ciInput$.pipe(
      debounceTime(1500),
      takeUntil(this.ciDestroy$),
    ).subscribe(ci => this.buscarPorCI(ci));
  }

  private buscarPorCI(ci: string): void {
    if (!ci || !this.form.ci) return;
    this.clienteService.get(`/pessoas/buscar?ci=${ci}&categoriaFornecedor=true&size=1`).subscribe({
      next: (res: any) => {
        const resultados = res?.content || [];
        if (resultados.length > 0) {
          const pessoa = resultados[0];
          this.pessoaJaCadastradaId = pessoa.id ?? null;
          this.pessoaJaCadastradaMensagem = this.translate.instant('fornecedor-add.mensagens.pessoa_ja_cadastrada', { nome: pessoa.nome });
          this.pessoaJaCadastradaDialogVisible = true;
        }
      },
    });
  }

  editarPessoaJaCadastrada(): void {
    this.pessoaJaCadastradaDialogVisible = false;
    if (this.pessoaJaCadastradaId) {
      this.router.navigate(['/fornecedores'], { queryParams: { edit: this.pessoaJaCadastradaId } });
    }
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  navigateListaDeFornecedores() {
    this.router.navigate(['lista-de-fornecedores']);
  }
}
